import {sortBy} from "es-toolkit";
import type {Category} from "../graphql";

export interface CategoryTreeNode extends Pick<Category, "id" | "title"> {
	children: CategoryTreeNode[];
}

/** If current depth more than max, collect recursive children into a flat list under the current node */
const flattenAtDepth = (
	node: CategoryTreeNode,
	currentDepth: number,
	maxDepth: number,
): void => {
	if (currentDepth >= maxDepth && node.children.length > 0) {
		// Collect all descendants recursively
		const allDescendants: CategoryTreeNode[] = [];
		const collectDescendants = (n: CategoryTreeNode) => {
			for (const child of n.children) {
				allDescendants.push({...child, children: []});
				collectDescendants(child);
			}
		};
		collectDescendants(node);

		// Replace children with flat list
		node.children = sortBy(allDescendants, ["title"]);
	} else {
		// Continue processing children recursively
		for (const child of node.children) {
			flattenAtDepth(child, currentDepth + 1, maxDepth);
		}
	}
};

/** Turn categories list into a tree, with a configurable max depth (child items below the specified depth are collapsed into one flat list). */
export function buildCategoryTree(
	categories: Category[],
	hierarchyDepth: number = 1,
	mapper: (category: CategoryTreeNode) => CategoryTreeNode | undefined = (c) =>
		c,
): CategoryTreeNode[] {
	const categoryMap = new Map<string, CategoryTreeNode>();
	const rootCategories: CategoryTreeNode[] = [];

	// First pass: create all nodes
	for (const category of categories) {
		const transformed = mapper({
			...category,
			children: [],
		});

		// Allow filtering out values
		if (transformed) {
			categoryMap.set(category.id, transformed);
		}
	}

	// Second pass: build tree structure
	for (const category of categories) {
		const node = categoryMap.get(category.id);

		if (!node) continue;

		if (category.parent?.id) {
			const parent = categoryMap.get(category.parent.id);
			if (parent) {
				parent.children.push(node);
			} else {
				// Parent not found, treat as root
				rootCategories.push(node);
			}
		} else {
			rootCategories.push(node);
		}
	}

	// Third pass: flatten children at specified depth
	for (const root of rootCategories) {
		flattenAtDepth(root, 1, hierarchyDepth);
	}

	return sortBy(rootCategories, ["title"]);
}
